import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Megaphone, Image as ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AnnouncementsPanel() {
  const [anns, setAnns] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/super-admin/announcements").then((r) => setAnns(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (id, val) => {
    await api.patch(`/super-admin/announcements/${id}`, { is_active: val });
    load();
  };
  const remove = async (id) => {
    if (!confirm("Delete this announcement?")) return;
    await api.delete(`/super-admin/announcements/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <section className="solid-panel p-0 overflow-hidden shadow-3d">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white">
        <div>
          <h3 className="text-xl font-heading font-semibold">Announcements & Posters</h3>
          <p className="text-sm text-slate-500">Push notifications or full-banner posters to clinic dashboards.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="open-add-announcement">
              <Plus className="h-4 w-4 mr-2" /> New announcement
            </Button>
          </DialogTrigger>
          <CreateDialog onClose={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      {anns.length === 0 && (
        <div className="p-12 text-center text-slate-400 text-sm">
          <Megaphone className="h-10 w-10 mx-auto opacity-40 mb-2" />
          No announcements yet. Create one to broadcast to clinic dashboards.
        </div>
      )}

      <div className="divide-y divide-slate-100">
        {anns.map((a) => (
          <div key={a.id} className="px-6 py-4 flex items-center gap-4" data-testid={`ann-row-${a.id}`}>
            <div className="h-14 w-14 rounded-xl bg-[#e6f0ff] overflow-hidden flex items-center justify-center flex-shrink-0">
              {a.image_url ? (
                <img src={a.image_url} alt="" className="h-full w-full object-cover" />
              ) : a.type === "poster" ? (
                <ImageIcon className="h-5 w-5 text-[#004a99]" />
              ) : (
                <Megaphone className="h-5 w-5 text-[#004a99]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 flex items-center gap-2">
                {a.title}
                <span className="text-xs font-bold uppercase tracking-wider text-[#f37021]">{a.type}</span>
              </div>
              <div className="text-xs text-slate-500 truncate">{a.body || a.cta_url || "—"}</div>
              <div className="text-xs text-slate-400">{a.target_clinic_ids?.length ? `${a.target_clinic_ids.length} targeted clinic(s)` : "All clinics"}</div>
            </div>
            <Switch checked={a.is_active} onCheckedChange={(v) => toggle(a.id, v)} data-testid={`ann-toggle-${a.id}`} />
            <Button variant="ghost" size="icon" onClick={() => remove(a.id)} data-testid={`ann-delete-${a.id}`}>
              <Trash2 className="h-4 w-4 text-slate-500" />
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}

function CreateDialog({ onClose }) {
  const [form, setForm] = useState({
    type: "notification", title: "", body: "", image_url: "",
    cta_label: "", cta_url: "", target_clinic_ids: [], is_active: true,
  });
  const [busy, setBusy] = useState(false);
  const [clinics, setClinics] = useState([]);

  useEffect(() => { api.get("/clinics").then((r) => setClinics(r.data)); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title) return toast.error("Title required");
    if (form.type === "poster" && !form.image_url) return toast.error("Poster needs image URL");
    setBusy(true);
    try {
      await api.post("/super-admin/announcements", form);
      toast.success("Announcement published");
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <DialogContent className="sm:max-w-[560px]" data-testid="ann-create-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading">New announcement</DialogTitle>
        <p className="text-sm text-slate-500">Notifications show as a top bar. Posters show as a full-width image banner.</p>
      </DialogHeader>

      <div className="space-y-3">
        <div className="flex gap-2">
          {["notification", "poster"].map((t) => (
            <button key={t} type="button"
                    onClick={() => set("type", t)}
                    className={`flex-1 rounded-xl border-2 p-3 text-sm font-semibold transition ${form.type === t ? "border-[#f37021] bg-[#ffefe6] text-[#f37021]" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`ann-type-${t}`}>
              {t === "poster" ? <ImageIcon className="h-4 w-4 inline mr-1" /> : <Megaphone className="h-4 w-4 inline mr-1" />}
              {t}
            </button>
          ))}
        </div>

        <div>
          <Label>Title *</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="New feature: WhatsApp prescription delivery 🎉" data-testid="ann-title" />
        </div>

        {form.type === "notification" && (
          <div>
            <Label>Body</Label>
            <Textarea value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="Short message shown in the banner." data-testid="ann-body" />
          </div>
        )}

        <div>
          <Label>Image URL {form.type === "poster" && "*"}</Label>
          <Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://..." data-testid="ann-image-url" />
          <p className="text-xs text-slate-400 mt-1">Paste any public image URL (use Unsplash, your CDN, etc.)</p>
        </div>

        {form.type === "notification" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>CTA label</Label>
              <Input value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} placeholder="Learn more" data-testid="ann-cta-label" />
            </div>
            <div>
              <Label>CTA URL</Label>
              <Input value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://..." data-testid="ann-cta-url" />
            </div>
          </div>
        )}

        <div>
          <Label>Target clinics (leave empty = all)</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {clinics.map((c) => {
              const on = form.target_clinic_ids.includes(c.id);
              return (
                <button key={c.id} type="button"
                        onClick={() => set("target_clinic_ids", on ? form.target_clinic_ids.filter((x) => x !== c.id) : [...form.target_clinic_ids, c.id])}
                        className={`text-xs rounded-full px-3 py-1.5 border transition ${on ? "bg-[#004a99] text-white border-[#004a99]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy} className="bg-[#f37021] hover:bg-[#d9621b] text-white" data-testid="ann-submit">
          {busy ? "Publishing…" : "Publish"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
